/**
 * Single-step submit: makeContact with LeadCreateInput + MakeContactMetaInput.
 */
import { getOrCreateUrqlClient } from '../graphql/client'
import {
  MakeContactWithMetaDocument,
  type MakeContactMetaInput,
} from '../graphql/makeContact.urql'
import { toMakeContactVariables } from '../graphql/mutations/makeContact'
import type { ContactMeSubmitPayload, ContactMeWidgetOptions } from '../types'
import type { FormState } from './useContactFormState'
import { buildMauticCookieHeader, getUtmFromLocation } from '../utils/mauticTracking'

function getPropertyIdFromContext(context?: Record<string, unknown>): string | undefined {
  if (!context || typeof context !== 'object') return undefined
  const id = context.propertyId ?? context.property_id
  return id != null ? String(id) : undefined
}

function getBrokerIdFromContext(context?: Record<string, unknown>): string | undefined {
  if (!context || typeof context !== 'object') return undefined
  const id =
    (context as Record<string, unknown>).brokerId ??
    (context as Record<string, unknown>).broker_id
  return id != null ? String(id) : undefined
}

function getDevOverlay() {
  return import.meta.env.DEV ? (window as any).__bostDevOverlay : null
}

export function useContactFormSubmit(
  _form: { value: FormState },
  optsRef: { value: ContactMeWidgetOptions },
  openDetailRef: { value: { context?: Record<string, unknown> } | undefined },
  isBusy: { value: boolean },
  setError: (key: keyof FormState, msg: string) => void,
  t: (key: string) => string,
  onSubmitted: (payload: ContactMeSubmitPayload) => void,
  onError: (err: string) => void
) {
  async function submit(
    buildPayload: () => ContactMeSubmitPayload,
    onSuccess: (conversationId?: string) => void
  ): Promise<void> {
    const payload = buildPayload()
    const propertyId = getPropertyIdFromContext(openDetailRef.value?.context)
    const brokerId = getBrokerIdFromContext(openDetailRef.value?.context)

    isBusy.value = true
    try {
      const utm = getUtmFromLocation()
      const meta: MakeContactMetaInput = {
        inboxUrl: typeof window !== 'undefined' ? window.location.href : '',
        utmSource: utm.utmSource,
        utmMedium: utm.utmMedium,
        utmCampaign: utm.utmCampaign,
        utmContent: utm.utmContent,
        utmTerm: utm.utmTerm,
        brokerId: brokerId || undefined,
        priceRange: payload.budget,
        purchaseTimeframe: payload.timeline,
        mauticCookieHeader: buildMauticCookieHeader() || undefined,
        hasWhatsApp: payload.hasWhatsApp,
      }

      const baseVars = toMakeContactVariables(payload, propertyId)
      const variables = { ...baseVars, meta }

      const devOverlay = getDevOverlay()
      if (devOverlay) {
        devOverlay.addMutation(
          'MakeContact',
          JSON.stringify(MakeContactWithMetaDocument),
          variables,
          async () => {
            const client = getOrCreateUrqlClient()
            return client.mutation(MakeContactWithMetaDocument, variables).toPromise()
          }
        )
        onSubmitted(payload)
        onSuccess('dev-mock-id')
      } else {
        const client = getOrCreateUrqlClient()
        const result = await client.mutation(MakeContactWithMetaDocument, variables).toPromise()
        if (result.error) {
          throw new Error(result.error.message)
        }
        const id = result.data?.makeContact?.id
        onSubmitted(payload)
        onSuccess(id != null ? String(id) : undefined)
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err))
      setError('email', t('errors.send_failed'))
    } finally {
      isBusy.value = false
    }
  }

  return { submit }
}
